import React, { useState } from "react";
import {
  Badge,
  Box,
  Button,
  ButtonDropdown,
  FormField,
  Input,
  Modal,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import * as tokens from "@amzn/awsui-design-tokens-core";
import { InvestigationLink } from "../../lusca-search-api/generated-src";
import { formatTimestamp } from "./investigationUtils";
import { SafeExternalLink } from "./SafeExternalLink";
import { linkDisplayLabel } from "./evidenceLinks";

/** Values sent back from the inline editor for an evidence link. */
export interface LinkEntryValues {
  url: string;
  label: string;
}

export interface InvestigationLinkListProps {
  entries: InvestigationLink[];
  /** Alias of the signed-in user, or null when unknown (no edit/delete offered). */
  currentUser: string | null;
  /** Investigation owner — may edit/delete every link, not just their own. */
  owner?: string | null;
  loading: boolean;
  /** Rendered when there are no links. */
  emptyState: React.ReactNode;
  onEdit: (
    entry: InvestigationLink,
    values: LinkEntryValues
  ) => Promise<unknown> | void;
  onDelete: (entry: InvestigationLink) => Promise<unknown> | void;
}

/** Links shown before the "Show all N" toggle. */
export const DEFAULT_VISIBLE_ENTRIES = 5;

const keyOf = (entry: InvestigationLink, index: number): string =>
  `${entry.url}-${entry.addedAt ?? ""}-${index}`;

/**
 * Newest first. Timestamps are ISO strings so lexical order is chronological; entries
 * without one keep their relative append order (later in the array = newer).
 */
const newestFirst = (entries: InvestigationLink[]): InvestigationLink[] =>
  entries
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => {
      const ta = a.entry.addedAt ?? "";
      const tb = b.entry.addedAt ?? "";
      if (ta !== tb) return ta < tb ? 1 : -1;
      return b.index - a.index;
    })
    .map(({ entry }) => entry);

const Secondary: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box color="text-body-secondary" fontSize="body-s" display="inline">
    {children}
  </Box>
);

const preWrap: React.CSSProperties = {
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  overflowWrap: "anywhere",
};

/**
 * Flag for entries that did not come from a person typing in the UI. Human posts are the
 * default and carry no badge; anything an agent (MCP) or the system wrote is called out.
 */
export const OriginBadge: React.FC<{ source?: string }> = ({ source }) => {
  if (!source || source === "ui") return null;
  if (source === "mcp") return <Badge color="blue">agent</Badge>;
  return <Badge color="grey">{source}</Badge>;
};

/** Adder · time · edited-by, above the link. */
const EntryHeader: React.FC<{
  entry: InvestigationLink;
  actions?: React.ReactNode;
}> = ({ entry, actions }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: "8px",
      flexWrap: "wrap",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        flexWrap: "wrap",
        flex: 1,
        minWidth: 0,
      }}
    >
      {(entry.source === "trackstars" || entry.addedBy) && (
        <Box fontSize="body-s" fontWeight="bold" display="inline">
          {entry.source === "trackstars" ? "TrackStars" : entry.addedBy}
        </Box>
      )}
      <Secondary>{formatTimestamp(entry.addedAt)}</Secondary>
      {entry.editedAt && (
        <Secondary>
          · edited{entry.editedBy ? ` by ${entry.editedBy}` : ""} ·{" "}
          {formatTimestamp(entry.editedAt)}
        </Secondary>
      )}
    </div>
    {actions}
  </div>
);

const LinkEditor: React.FC<{
  entry: InvestigationLink;
  loading: boolean;
  onCancel: () => void;
  onSave: (values: LinkEntryValues) => void;
}> = ({ entry, loading, onCancel, onSave }) => {
  const [url, setUrl] = useState(entry.url);
  const [label, setLabel] = useState(entry.label ?? "");
  const nextUrl = url.trim();
  const nextLabel = label.trim() || nextUrl;
  const unchanged =
    nextUrl === entry.url && nextLabel === (entry.label || entry.url);
  return (
    <SpaceBetween size="xs">
      <FormField label="URL" stretch>
        <Input
          value={url}
          onChange={({ detail }) => setUrl(detail.value)}
          ariaLabel="Edit link URL"
          autoFocus
        />
      </FormField>
      <FormField
        label="Label"
        description="Optional. Label defaults to the URL."
        stretch
      >
        <Input
          value={label}
          onChange={({ detail }) => setLabel(detail.value)}
          ariaLabel="Edit link label"
        />
      </FormField>
      <Box float="right">
        <SpaceBetween direction="horizontal" size="xs">
          <Button variant="link" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!nextUrl || unchanged}
            loading={loading}
            onClick={() => onSave({ url: nextUrl, label: nextLabel })}
          >
            Save
          </Button>
        </SpaceBetween>
      </Box>
    </SpaceBetween>
  );
};

/** Read-only presentation of one link. */
const LinkContent: React.FC<{ entry: InvestigationLink }> = ({ entry }) => {
  const label = linkDisplayLabel(entry);
  return (
    <Box>
      <div style={preWrap}>
        <SafeExternalLink href={entry.url}>{label}</SafeExternalLink>
      </div>
      {label !== entry.url && (
        <div style={preWrap}>
          <Secondary>{entry.url}</Secondary>
        </div>
      )}
    </Box>
  );
};

/**
 * Compact, newest-first list of evidence links with inline edit and confirmed delete for
 * the adder or the investigation owner. Long lists show the most recent few with a
 * "Show all" toggle so a busy investigation stays a readable summary.
 */
export const InvestigationLinkList: React.FC<InvestigationLinkListProps> = ({
  entries,
  currentUser,
  owner,
  loading,
  emptyState,
  onEdit,
  onDelete,
}) => {
  const [showAll, setShowAll] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<InvestigationLink | null>(null);

  const ordered = newestFirst(entries);
  if (ordered.length === 0) return <Box>{emptyState}</Box>;

  const visible = showAll ? ordered : ordered.slice(0, DEFAULT_VISIBLE_ENTRIES);
  const hidden = ordered.length - visible.length;

  const mayManage = (entry: InvestigationLink): boolean =>
    !!currentUser &&
    (currentUser === entry.addedBy || (!!owner && currentUser === owner));

  const finishEdit = (entry: InvestigationLink, values: LinkEntryValues) => {
    Promise.resolve(onEdit(entry, values))
      .then(() => setEditingKey(null))
      .catch(console.error);
  };

  const confirmDelete = () => {
    if (!deleting) return;
    Promise.resolve(onDelete(deleting))
      .then(() => setDeleting(null))
      .catch(console.error);
  };

  return (
    <SpaceBetween size="xs">
      {visible.map((entry, index) => {
        const key = keyOf(entry, index);
        const editing = editingKey === key;
        return (
          <React.Fragment key={key}>
            {index > 0 && (
              <div
                role="separator"
                style={{
                  borderTop: `1px solid ${tokens.colorBorderDividerDefault}`,
                }}
              />
            )}
            <div data-testid="entry-row">
              <SpaceBetween size="xxs">
                <EntryHeader
                  entry={entry}
                  actions={
                    !editing &&
                    mayManage(entry) && (
                      <ButtonDropdown
                        variant="inline-icon"
                        expandToViewport
                        ariaLabel="Actions for link"
                        items={[
                          { id: "edit", text: "Edit" },
                          { id: "delete", text: "Delete" },
                        ]}
                        onItemClick={({ detail }) => {
                          if (detail.id === "edit") setEditingKey(key);
                          if (detail.id === "delete") setDeleting(entry);
                        }}
                      />
                    )
                  }
                />
                {editing ? (
                  <LinkEditor
                    entry={entry}
                    loading={loading}
                    onCancel={() => setEditingKey(null)}
                    onSave={(values) => finishEdit(entry, values)}
                  />
                ) : (
                  <LinkContent entry={entry} />
                )}
              </SpaceBetween>
            </div>
          </React.Fragment>
        );
      })}
      {(hidden > 0 || showAll) && ordered.length > DEFAULT_VISIBLE_ENTRIES && (
        <Button variant="inline-link" onClick={() => setShowAll((v) => !v)}>
          {showAll ? "Show fewer" : `Show all ${ordered.length}`}
        </Button>
      )}
      {deleting && (
        <Modal
          visible
          size="small"
          onDismiss={() => setDeleting(null)}
          header="Delete link"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={() => setDeleting(null)}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  loading={loading}
                  onClick={confirmDelete}
                >
                  Delete
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          Delete this link? This is recorded in the audit log.
        </Modal>
      )}
    </SpaceBetween>
  );
};

export default InvestigationLinkList;
